import { Controller } from "@hotwired/stimulus";

/**
 * Modal Manager Controller
 * 
 * PURPOSE:
 * This controller manages the display and queuing of multiple modals throughout the application.
 * It ensures that modals are shown in the correct order based on priority and prevents modal
 * conflicts by implementing a queue system.
 * 
 * BEHAVIOR:
 * - Listens for 'modal:show' and 'modal:hide' custom events dispatched by individual modals
 * - Maintains a queue of pending modals sorted by priority (higher priority first)
 * - Only shows one modal at a time unless modals are marked as non-queueable
 * - Automatically shows the next modal in queue when the current one is dismissed
 * - Handles z-index stacking to ensure proper layering when multiple modals are active
 * - Prevents body scrolling when any modal is active
 * - Has a 2-second initialization period where queueable modals are held as pending
 * - Includes 1-second delays between modal transitions for better UX
 * 
 * USAGE:
 * - Individual modals dispatch 'modal:show' events with modalId, priority, and queueable properties
 * - Modals can be marked as queueable (default: false) to participate in the queue system
 * - Priority determines order in queue (higher numbers = higher priority)
 * - Non-queueable modals will show immediately, potentially interrupting the queue
 * 
 * This system is particularly useful for welcome modals, notifications, and other
 * sequential user interactions that need to be presented in a controlled manner.
 */
export default class extends Controller {
  connect() {
    this.modalQueue = [];
    this.activeModals = new Set();
    this.baseZIndex = 100;
    this.isInitializing = true;
    this.pendingModals = [];
    
    this.boundHandleModalShow = this.handleModalShow.bind(this);
    this.boundHandleModalHide = this.handleModalHide.bind(this);
    
    document.addEventListener('modal:show', this.boundHandleModalShow);
    document.addEventListener('modal:hide', this.boundHandleModalHide);

    setTimeout(() => {
      this.isInitializing = false;
      this.processPendingModals();
    }, 2000);
  }

  disconnect() {
    document.removeEventListener('modal:show', this.boundHandleModalShow);
    document.removeEventListener('modal:hide', this.boundHandleModalHide);
  }

  handleModalShow(event) {
    const { modalId, priority = 0, queueable = false } = event.detail;
    
    if (this.isInitializing && queueable) {
      this.pendingModals.push({ modalId, priority });
      return;
    }
    
    if (this.activeModals.size > 0 && queueable) {
      this.addToQueue(modalId, priority);
    } else {
      this.showModal(modalId, priority);
    }
  }

  async processPendingModals() {
    if (this.pendingModals.length === 0) {
      return;
    }

    this.pendingModals.sort((a, b) => b.priority - a.priority);
    
    const first = this.pendingModals.shift();
    
    await this.delay(1000);
    this.showModal(first.modalId, first.priority);
    
    this.pendingModals.forEach(modal => {
      this.addToQueue(modal.modalId, modal.priority);
    });
    
    this.pendingModals = [];
  }

  async handleModalHide(event) {
    const { modalId } = event.detail;
    
    this.activeModals.delete(modalId);
    
    if (this.modalQueue.length > 0) {
      const next = this.modalQueue.shift();
      await this.delay(1000);
      this.showModal(next.modalId, next.priority);
    }
    
    if (this.activeModals.size === 0) {
      document.body.style.overflow = '';
    }
  }

  showModal(modalId, priority) {
    const modal = document.getElementById(modalId);
    if (!modal) {
      return;
    }

    this.activeModals.add(modalId);
    
    const zIndex = this.baseZIndex + (priority * 10) + (this.activeModals.size * 5);
    modal.style.zIndex = zIndex;
    modal.classList.remove('hidden');
    modal.classList.add('flex');
    document.body.style.overflow = 'hidden';
    
    document.dispatchEvent(new CustomEvent('modal:shown', { 
      detail: { modalId, zIndex, priority } 
    }));
  }

  addToQueue(modalId, priority = 0) {
    if (this.modalQueue.find(item => item.modalId === modalId)) {
      return;
    }
    
    this.modalQueue.push({ modalId, priority });
    this.modalQueue.sort((a, b) => b.priority - a.priority);
  }

  clearQueue() {
    this.modalQueue = [];
  }

  getActiveModals() {
    return Array.from(this.activeModals);
  }

  getQueue() {
    return [...this.modalQueue];
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
